/* One queue owns every theme cart write. Section HTML and cart data arrive together. */
(function () {
  'use strict';
  if (window.ThemeCart) return;
  let queue = Promise.resolve(), pending = 0, refreshing;
  const configNode = document.querySelector('[data-cart-config]');
  const config = configNode ? JSON.parse(configNode.textContent) : {};
  const routes = window.theme.routes;
  let cart = config.cart, accepted = false, uncertain = false;
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  const message = key => config.messages?.[key] || key;
  function counts() {
    if (!cart) return;
    document.querySelectorAll('cart-counter').forEach(el => el.setCount?.(cart.item_count));
    document.querySelectorAll('[data-cart-price]').forEach(el => { el.textContent = window.theme.money.format(cart.total_price); el.dataset.cartPrice = cart.total_price; });
  }
  function publish(next, source) {
    cart = next; uncertain = false;
    counts();
    emit('cart:updated', { cart, source, confirmed: true });
  }
  function enqueue(work) {
    pending++;
    document.documentElement.dataset.cartPending = 'true';
    const result = queue.then(work);
    queue = result.catch(() => {}).finally(() => {
      pending--;
      document.documentElement.dataset.cartPending = String(pending > 0);
    });
    return result;
  }
  function sectionIds() {
    return [...new Set(['cart-overlays', ...Array.from(document.querySelectorAll('.cart-surface'), el => el.dataset.cartSectionId)])].filter(Boolean).slice(0, 5);
  }
  async function read() {
    const response = await fetch(routes.cart + '.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(message('failed'));
    return response.json();
  }
  async function sections(next, ids) {
    next.sections = next.sections || {};
    const missing = ids.filter(id => !next.sections[id]);
    if (!missing.length) return next;
    const url = new URL(location.href); url.searchParams.set('sections', missing.join(','));
    try {
      const response = await fetch(url.pathname + url.search, { credentials: 'same-origin', cache: 'no-store' });
      if (response.ok) Object.assign(next.sections, await response.json());
    } catch (_) { /* A successful write stays successful even when rendering fails. */ }
    return next;
  }
  async function write(kind, body, source) {
    const before = cart;
    const identity = item => JSON.stringify([String(item.variant_id || item.id), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '')]);
    const ids = sectionIds();
    const formData = body instanceof FormData;
    const singleQuantity = formData && body.has('id') ? Number(body.get('quantity') || 1) : null;
    const requestedItems = formData ? [] : body.items || [body];
    if (formData) { body.set('sections', ids.join(',')); body.set('sections_url', location.pathname); }
    else body = { ...body, sections: ids, sections_url: location.pathname };
    let response;
    try {
      response = await fetch(routes[kind] + '.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(formData ? {} : { 'Content-Type': 'application/json' }) },
        body: formData ? body : JSON.stringify(body)
      });
    } catch (_) {
      uncertain = true;
      // Never retry a write automatically: the server may already have applied it.
      try { publish(await sections(await read(), ids), 'reconcile'); } catch (_) {}
      throw new Error(message('uncertain'));
    }
    let data;
    try { data = await response.json(); }
    catch (_) {
      uncertain = true;
      try { publish(await sections(await read(), ids), 'reconcile'); } catch (_) {}
      throw new Error(message('uncertain'));
    }
    if (!response.ok) {
      uncertain = true;
      // Shopify may add the available stock even when it returns a stock error.
      try { publish(await sections(await read(), ids), 'reconcile'); } catch (_) {}
      throw new Error(typeof data.description === 'string' ? data.description : data.message || message('failed'));
    }
    let next = data;
    if (kind === 'cartAdd') {
      const html = data.sections?.['cart-overlays'];
      const node = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-config]');
      try { next = node ? JSON.parse(node.textContent).cart : await read(); }
      catch (_) { uncertain = true; (data.items || [data]).forEach(item => { item.quantity = null; }); return { added: data, stale: true }; }
      next.sections = data.sections;
      const added = (data.items || [data]).filter((item, index, items) => items.findIndex(other => identity(other) === identity(item)) === index);
      if (data.items) data.items = added;
      added.forEach(item => {
        const previous = (before?.items || []).filter(old => identity(old) === identity(item)).reduce((sum, old) => sum + old.quantity, 0);
        const current = next.items.filter(line => identity(line) === identity(item)).reduce((sum, line) => sum + line.quantity, 0);
        const requested = requestedItems.filter(entry => identity(entry) === identity(item)).reduce((sum, entry) => sum + Number(entry.quantity || 1), 0);
        // A 200 response accepted the requested amount. Another tab may have changed
        // the previous cart, so use the submitted quantity whenever it is available.
        item.quantity = singleQuantity !== null ? singleQuantity : requested || Math.max(0, current - previous);
      });
    }
    await sections(next, ids);
    publish(next, source);
    return { cart: next, added: kind === 'cartAdd' ? data : null };
  }
  const api = window.ThemeCart = {
    config, message, get cart() { return cart; }, get pending() { return pending; },
    get accepted() { return accepted; },
    accept(value) { accepted = value; emit('cart:terms', { accepted }); },
    idle: () => queue,
    change: (body, source) => enqueue(() => write('cartChange', body, source)),
    update: (body, source) => enqueue(() => write('cartUpdate', body, source)),
    add: (body, source) => enqueue(() => write('cartAdd', body, source)),
    refresh() {
      if (!refreshing) refreshing = enqueue(async () => {
        const next = await sections(await read(), sectionIds()); publish(next, 'refresh'); return next;
      }).finally(() => { refreshing = null; });
      return refreshing;
    },
    open(trigger) {
      const overlay = document.querySelector('cart-overlays');
      if ((config.destination === 'sidebar' || config.destination === 'popup') && overlay) overlay.open(trigger, config.destination);
      else location.assign(routes.cart);
    },
    async checkout(trigger) {
      await window.ThemeCartForms?.saveNotes();
      await queue;
      if (uncertain) await api.refresh();
      if (!cart?.item_count) { api.open(trigger); return; }
      if (config.requireTerms && !accepted) {
        const visibleTerms = () => [...document.querySelectorAll('[data-cart-terms]')].find(input => input.getClientRects().length);
        if (!visibleTerms()) api.open(trigger);
        const input = visibleTerms();
        if (input) {
          const status = input.closest('form')?.querySelector('[data-cart-status]');
          if (status) { status.textContent = message('terms_error'); status.classList.add('is-error'); }
          input.focus();
        }
        return;
      }
      location.assign(routes.root + 'checkout');
    }
  };
  function status(form, text) {
    let el = form.querySelector('[data-pl-status], [data-pl-card-status], [data-cart-add-status], .added-msg');
    if (!el) { el = document.createElement('p'); el.dataset.cartAddStatus = ''; el.setAttribute('role', 'status'); form.append(el); }
    el.setAttribute('role', 'status'); el.style.display = ''; el.textContent = text;
  }
  const quickAdds = new WeakMap();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const url = new URL(form.action, location.href);
    if (url.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    // Product options are resolved by the server before their variant can be submitted.
    if (form.dataset.optionsPending === 'true' || form.dataset.sizePending === 'true') return;
    const repeatable = form.hasAttribute('data-pl-quick-add');
    if ((!repeatable && form.dataset.cartAdding === 'true') || !form.reportValidity()) return;
    const data = new FormData(form);
    const button = event.submitter || form.querySelector('[type="submit"]');
    const buttonLabel = button?.querySelector('[data-cart-button-label]') || button;
    let quick;
    if (repeatable && button) {
      quick = quickAdds.get(form);
      if (!quick) {
        quick = { pending: 0, label: buttonLabel.textContent, minWidth: button.style.minWidth, timer: null };
        quickAdds.set(form, quick);
      }
      clearTimeout(quick.timer);
      delete form.dataset.cartFeedback;
      // Keep the shorter confirmation from moving the target under the pointer.
      if (quick.pending === 0) button.style.minWidth = getComputedStyle(button).width;
      quick.pending++;
      if (buttonLabel.textContent !== message('added')) buttonLabel.textContent = message('adding');
      status(form, '');
    }
    form.dataset.cartAdding = 'true'; form.setAttribute('aria-busy', 'true');
    const wasDisabled = button?.disabled;
    if (button && !quick) button.disabled = true;
    if (!quick) status(form, message('adding'));
    try {
      const result = await api.add(data, 'add');
      if (quick) {
        buttonLabel.textContent = message('added');
        form.dataset.cartFeedback = 'true';
        if (result.stale) status(form, message('check_total'));
      } else {
        status(form, result.stale ? message('added') + ' ' + message('check_total') : message('added'));
      }
      const items = result.added.items || [result.added];
      const overlay = document.querySelector('cart-overlays');
      if (config.afterAdd === 'page') location.assign(routes.cart);
      else if (config.afterAdd === 'sidebar' && overlay) { if (result.stale) overlay.confirm(items, button, true); else overlay.open(button); }
      else if (config.afterAdd === 'popup' && overlay) overlay.confirm(items, button, result.stale);
    } catch (error) {
      if (quick) buttonLabel.textContent = quick.label;
      status(form, error.message);
    }
    finally {
      if (quick) quick.pending--;
      if (!quick || quick.pending === 0) {
        delete form.dataset.cartAdding; form.removeAttribute('aria-busy');
        if (quick) {
          // Every click joins the cart queue; only the label has a cooldown.
          quick.timer = setTimeout(() => {
            buttonLabel.textContent = quick.label;
            button.style.minWidth = quick.minWidth;
            delete form.dataset.cartFeedback;
          }, 1200);
        }
      }
      if (button && !quick) {
        const picker = form.querySelector('[data-product-option-picker]');
        button.disabled = form.dataset.optionsPending === 'true' || (picker ? picker.dataset.selectedVariantAvailable !== 'true' : wasDisabled);
      }
    }
  }, true);
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]'); if (!link || link.target === '_blank' || link.hasAttribute('download') || link.hasAttribute('data-cart-page')) return;
    const url = new URL(link.href, location.href); if (url.origin !== location.origin) return;
    if (link.hasAttribute('data-cart-checkout') || link.hasAttribute('data-header-checkout') || /\/checkout\/?$/.test(url.pathname)) {
      event.preventDefault(); event.stopImmediatePropagation(); api.checkout(link).catch(() => location.assign(routes.cart)); return;
    }
    if (url.pathname.replace(/\/$/, '') !== routes.cart.replace(/\/$/, '') || url.search) return;
    if (!['sidebar', 'popup'].includes(config.destination) || location.pathname === routes.cart) return;
    event.preventDefault(); event.stopImmediatePropagation(); api.open(link);
  }, true);
  window.addEventListener('cart:updated', event => {
    if (event.detail?.confirmed) return;
    // A single compatibility bridge for third-party/older theme callers.
    api.refresh().catch(() => {});
  });
  window.addEventListener('pageshow', event => { if (event.persisted) api.refresh().catch(() => {}); });
  counts();
})();
